import React, { useState, useMemo, useEffect, useCallback } from 'react';
import ERPAdvancedGrid    from './ERPAdvancedGrid';
import useERPContextMenu  from './ERPContextMenu';
import ERPEditModal       from './ERPEditModal';
import { erpToast }       from './ERPToast';
import '../../styles/erp-theme.css';
import { API, apiFetch } from '../../lib/api';

/**
 * SimpleERPGrid — drop-in replacement for plain <table> elements.
 *
 * Self-contained: handles client-side pagination, sorting, filtering, plus
 * context-menu + (optional) Edit modal via the Licensor mutation API.
 *
 * Props:
 *   rows           (array)      Rows to render (already-fetched).
 *   columns        (array)      [{ field, header, visible?, frozen? }]
 *   idField        (string)     Primary-key field on each row.
 *   nameField      (string)     Display field (used in three-dot menu header).
 *   title          (string)     Grid title (top-left).
 *   testIdBase     (string)     `data-testid` prefix.
 *   loading        (bool)
 *   onRefresh      (fn)
 *
 *   // Editing (optional — only if super admin should be able to edit)
 *   mutationTable  (string)     e.g. 'ClientMaster_License' — table for PUT
 *   pk             (string)     PK column to use in PUT URL
 *
 *   // Custom row actions
 *   copyKeys       (array)      [{field,label}] used by context menu
 *   voidAction     (object)     { endpoint: (row)=>'/x/y/void', label: '...' }
 *   onRowClick     (fn)
 *   defaultPageSize(number)
 */
export function SimpleERPGrid({
  rows = [],
  columns = [],
  idField,
  nameField,
  title,
  testIdBase,
  loading = false,
  onRefresh,
  mutationTable = null,
  pk,
  copyKeys = [],
  voidAction,
  onRowClick,
  defaultPageSize = 20
}) {
  const [page, setPage]         = useState(1);
  const [pageSize, setPageSize] = useState(defaultPageSize);
  const [sortField, setSortField] = useState(null);
  const [sortDir, setSortDir]   = useState('asc');
  const [filters, setFilters]   = useState({});
  const [editing, setEditing]   = useState(null);

  // Reset page when row data changes
  useEffect(() => { setPage(1); }, [rows.length]);

  // Filter + sort
  const filteredSorted = useMemo(() => {
    let r = rows;
    const active = Object.entries(filters).filter(([_, v]) => v != null && String(v).trim() !== '');
    if (active.length) {
      r = r.filter(row => active.every(([k, v]) =>
        String(row[k] ?? '').toLowerCase().includes(String(v).toLowerCase())));
    }
    if (sortField) {
      r = [...r].sort((a, b) => {
        const av = a[sortField], bv = b[sortField];
        if (av == null && bv == null) return 0;
        if (av == null) return 1;
        if (bv == null) return -1;
        if (typeof av === 'number' && typeof bv === 'number') return sortDir === 'asc' ? av - bv : bv - av;
        return sortDir === 'asc' ? String(av).localeCompare(String(bv)) : String(bv).localeCompare(String(av));
      });
    }
    return r;
  }, [rows, filters, sortField, sortDir]);

  const total = filteredSorted.length;
  const paginated = useMemo(
    () => filteredSorted.slice((page - 1) * pageSize, page * pageSize),
    [filteredSorted, page, pageSize]
  );

  const onSort = (field) => {
    if (sortField === field) setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    else { setSortField(field); setSortDir('asc'); }
  };

  // Edit handlers
  const openEdit = useCallback((row, mode = 'edit') => {
    setEditing({ row, mode: mode === 'view' || !mutationTable ? 'view' : 'edit' });
  }, [mutationTable]);

  const onVoid = useCallback(async (row) => {
    if (!voidAction) return;
    const reason = window.prompt(`Reason to ${voidAction.label} for ${row[idField]}?`, 'Performed by Super Admin');
    if (reason == null) return;
    try {
      const res = await apiFetch(`${API}${voidAction.endpoint(row)}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ reason })
      });
      const data = await res.json();
      if (!res.ok || data.error) erpToast.error(`Failed: ${data.error || res.status}`);
      else { erpToast.success(`${voidAction.label} successful`); onRefresh && onRefresh(); }
    } catch (e) { erpToast.error(`Failed: ${e.message}`); }
  }, [voidAction, idField, onRefresh]);

  const onPreview = useCallback((row) => {
    const lines = Object.entries(row).map(([k, v]) => `${k}: ${v ?? ''}`).join('\n');
    const w = window.open('', '_blank');
    if (w) w.document.write(`<pre style="font-family:monospace;font-size:12px;padding:20px;white-space:pre-wrap">${lines.replace(/</g, '&lt;')}</pre>`);
  }, []);

  const onEmail = useCallback((row) => {
    const email = row.EmailId || row.Email || row.email;
    if (!email) { erpToast.warning('No email on this row'); return; }
    window.location.href = `mailto:${email}?subject=Regarding ${row[idField]}`;
  }, [idField]);

  const onRowExport = useCallback((row) => {
    const b = new Blob([Object.entries(row).map(([k, v]) => `${k}: ${v}`).join('\n')], { type: 'text/plain' });
    const a = document.createElement('a');
    a.href = URL.createObjectURL(b); a.download = `${row[idField] || 'row'}.txt`; a.click();
    erpToast.success('Row exported');
  }, [idField]);

  const onOpenInNewTab = useCallback((row) => {
    const params = new URLSearchParams({ table: testIdBase || 'grid', id: String(row[idField] ?? '') });
    window.open(`${window.location.origin}/?detail=${encodeURIComponent(params.toString())}`, '_blank');
  }, [testIdBase, idField]);

  const { onRowContextMenu, ContextMenuPortal } = useERPContextMenu({
    idField,
    copyKeys,
    onView:    (r) => openEdit(r, 'view'),
    onEdit:    mutationTable ? (r) => openEdit(r, 'edit') : undefined,
    onVoid:    voidAction ? onVoid : undefined,
    onPreview,
    onPrint:   () => window.print(),
    onExport:  onRowExport,
    onEmail,
    onOpenInNewTab
  });

  // Hook context-menu to row right-click via DOM delegate
  useEffect(() => {
    const handler = (e) => {
      const tr = e.target.closest(`tr[data-testid^="${testIdBase}-row-"]`);
      if (!tr) return;
      const idAttr = tr.getAttribute('data-testid');
      const id = idAttr.replace(`${testIdBase}-row-`, '');
      const row = paginated.find(r => String(r[idField]) === id);
      if (row) onRowContextMenu(e, row);
    };
    document.addEventListener('contextmenu', handler);
    return () => document.removeEventListener('contextmenu', handler);
  }, [testIdBase, paginated, idField, onRowContextMenu]);

  return (
    <div data-testid={`${testIdBase}-wrapper`}>
      <ERPAdvancedGrid
        title={title}
        columns={columns}
        data={paginated}
        total={total}
        idField={idField}
        nameField={nameField}
        page={page}
        pageSize={pageSize}
        onPageChange={setPage}
        onPageSizeChange={setPageSize}
        sortField={sortField}
        sortDir={sortDir}
        onSort={onSort}
        filters={filters}
        onFilterChange={(f) => { setFilters(f); setPage(1); }}
        loading={loading}
        onEdit={(row, mode) => openEdit(row, mode === 'view' ? 'view' : (mutationTable ? 'edit' : 'view'))}
        onVoid={voidAction ? onVoid : undefined}
        onPreview={onPreview}
        onEmail={onEmail}
        onRowExport={onRowExport}
        onRowClick={onRowClick}
        showAddNew={false}
        testId={testIdBase}
      />
      {ContextMenuPortal}
      {editing && (
        <ERPEditModal
          table={mutationTable || 'ClientMaster'}
          row={editing.row}
          pk={pk || idField}
          readOnly={editing.mode === 'view' || !mutationTable}
          title={`${editing.mode === 'view' ? 'View' : 'Edit'} — ${editing.row[idField] ?? ''}`}
          onClose={() => setEditing(null)}
          onSaved={() => { setEditing(null); onRefresh && onRefresh(); }}
        />
      )}
    </div>
  );
}

export default SimpleERPGrid;
